import { intersection, notUndefined } from '@go-shared/lib/functional';
import { hashStringToColor } from '@go-shared/lib/utils';
import { Property, PropertyTag } from '@go-shared/types/property';
import { SimpleTemplate } from '@go-shared/types/template';
import Fuse from 'fuse.js';
import React, { useMemo, useState } from 'react';

import {
  BubbleBadge,
  BubbleButton,
  BubbleInput,
  BubbleSelect,
  BubbleTable,
  BubbleTypeahead,
  BubbleFormGroup,
} from '../BubbleUI';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from '../BubbleUI/BubbleTable';
import { BubbleTypeaheadValue } from '../BubbleUI/BubbleTypeahead';
import { getPropertyConfiguration } from '@go-shared/lib/propertyConfig';
import { FormGenericContainer } from '../FormGenericContainer';
import { BareSimpleSailingDayBundle } from '@go-shared/types/sailing';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import { DayMarkersButton } from '../DayMarkersButton';

interface TemplateSelectPageProps {
  title?: string;
  property: Property;
  sailingBundles: BareSimpleSailingDayBundle[];
  templates: SimpleTemplate[];
  selectedTemplates: SimpleTemplate[];
  onSelectedTemplatesChange?: (templates: SimpleTemplate[]) => void;
  onGoBack?: () => void;
  onGoNext?: () => void;
  onClose?: () => void;
}

interface TemplateSelectPageFilters {
  tag: Array<BubbleTypeaheadValue<PropertyTag>>;
  type: string | undefined;
  selectedOnly: boolean;
}

interface TemplateSelectPageTableItem {
  id: string;
  checked: boolean;
  name: string;
  type: string;
  eventsCount: number;
  venueDataCount: number;
  contentCount: number;
  tags: PropertyTag[];
  _source: SimpleTemplate;
}

export default function TemplateSelectPage(props: TemplateSelectPageProps) {
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<TemplateSelectPageFilters>({
    tag: [],
    type: undefined,
    selectedOnly: false,
  });

  const tableItems: TemplateSelectPageTableItem[] = useMemo(
    () =>
      props.templates.map(t => ({
        id: t._id,
        checked: props.selectedTemplates.findIndex(st => st._id === t._id) >= 0,
        name: t.name,
        type: t.dayType,
        eventsCount: t.eventsCount,
        venueDataCount: t.venueDataCount,
        contentCount: t.contentCount,
        tags: t.tags,
        _source: t,
      })),
    [props.templates, props.selectedTemplates]
  );

  const fuse = useMemo(() => {
    return new Fuse(tableItems, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['name'],
    });
  }, [tableItems]);

  const fuseSearchResults = useMemo(() => {
    if (!search) return undefined;
    return fuse.search(search).map(s => s.item);
  }, [fuse, search]);

  const tableItemsFiltered = useMemo(() => {
    if (filters.selectedOnly) {
      return tableItems.filter(
        ti => props.selectedTemplates.findIndex(sti => sti._id === ti.id) > -1
      );
    }
    let results = fuseSearchResults || tableItems;

    if (filters.type && filters.type.length > 0) {
      results = results.filter(r => r.type.toLowerCase() === filters.type!.toLowerCase());
    }

    if (filters.tag.length > 0) {
      results = results.filter(
        r =>
          intersection(
            r.tags.map(t => t._id),
            filters.tag.map(f => f.item._id)
          ).length === filters.tag.length
      );
    }

    return results;
  }, [fuseSearchResults, tableItems, filters]);

  const allSelected =
    tableItemsFiltered.length > 0 && tableItemsFiltered.findIndex(tif => !tif.checked) < 0;

  const showType = getPropertyConfiguration(props.property.type, 'showType');
  const sailingLabel = getPropertyConfiguration(props.property.type, 'sailingLabel');
  const tableFields: Array<BubbleTableFields<TemplateSelectPageTableItem>> = [
    {
      key: 'select',
      label: '',
      render: () => (
        <BubbleInput
          variant="inverse"
          inputClassName="stretched-link"
          type="checkbox"
          checked={allSelected}
          onValue={handleSelectAll}
        />
      ),
    },
    { key: 'name' },
    {
      key: 'metadata',
      label: 'Stats',
      behavior: BubbleTableFieldBehavior.Condense,
    },
    {
      key: 'markers',
      behavior: BubbleTableFieldBehavior.Condense,
    },
    {
      key: 'tags',
      behavior: BubbleTableFieldBehavior.Condense,
    },
  ].filter(notUndefined);

  const tableTemplates: Array<BubbleTableTemplate<TemplateSelectPageTableItem>> = [
    {
      key: 'select',
      render: item => (
        <BubbleInput
          type="checkbox"
          inputClassName="stretched-link"
          checked={item.checked}
          onValue={(checked: boolean) => {
            if (!props.onSelectedTemplatesChange) return;

            if (checked) {
              props.onSelectedTemplatesChange([...props.selectedTemplates, item._source]);
            } else {
              props.onSelectedTemplatesChange(
                props.selectedTemplates.filter(st => st._id !== item._source._id)
              );
            }
          }}
        />
      ),
    },
    {
      key: 'tags',
      render: ({ tags }) => (
        <div>
          {tags.map(t => (
            <BubbleBadge key={t._id} className="mr-1" color={hashStringToColor(t.name)}>
              {t.name}
            </BubbleBadge>
          ))}
        </div>
      ),
    },
    {
      key: 'markers',
      render: item => {
        return <DayMarkersButton template={item._source} />;
      },
    },
    {
      key: 'metadata',
      render: ({ eventsCount, venueDataCount, contentCount }) => (
        <>
          <BubbleBadge variant={eventsCount > 0 ? 'lime' : 'smoke'} className="mt-1 mr-1">
            E: {eventsCount}
          </BubbleBadge>
          <BubbleBadge variant={venueDataCount > 0 ? 'banana' : 'smoke'} className="mt-1 mr-1">
            VD: {venueDataCount}
          </BubbleBadge>
          <BubbleBadge variant={contentCount > 0 ? 'ocean' : 'smoke'} className="mt-1 mr-1">
            C: {contentCount}
          </BubbleBadge>
        </>
      ),
    },
  ];

  const templateTypeOpts = ['sea', 'port'];
  const tagOptions = props.property.tags.map(t => ({ key: t._id, item: t }));

  function handleSelectAll(checked: boolean) {
    if (!props.onSelectedTemplatesChange) return;

    if (checked) {
      props.onSelectedTemplatesChange([
        ...props.selectedTemplates,
        ...tableItemsFiltered.map(tif => tif._source),
      ]);
    } else {
      props.onSelectedTemplatesChange(
        props.selectedTemplates.filter(
          st => tableItemsFiltered.findIndex(tif => tif._source._id !== st._id) < 0
        )
      );
    }
  }

  // we use sets to find a unique sailing from a list of sailing bundles since this component
  // can work via a single apply and a bulk apply (so it needs to know how to work with
  // only one sailing and only one day as well as multiple sailings and multiple days)
  const sailingSet = new Set<string>();
  const daySet = new Set<string>();

  props.sailingBundles.forEach(bnd => {
    sailingSet.add(bnd.sailing.name);
    daySet.add(bnd.day.name);
  });

  return (
    <div className="tflf-4">
      <FormGenericContainer
        footer={() => (
          <>
            <div className="text-slate-dark">
              {sailingSet.size === 1 && (
                <div>
                  {sailingLabel} Selected:
                  <span className="font-semibold"> {sailingSet.values().next().value}</span>
                  {daySet.size === 1 && (
                    <span className="font-semibold"> (Day {daySet.values().next().value})</span>
                  )}
                </div>
              )}
            </div>

            <BubbleButton variant="navy" inverse className="ml-auto" onClick={props.onGoBack}>
              Go Back
            </BubbleButton>
            <BubbleButton
              disabled={props.selectedTemplates.length <= 0}
              variant="navy"
              onClick={props.onGoNext}
            >
              Next: Map Fragments To Day(s)
            </BubbleButton>
          </>
        )}
      >
        <div className="flex items-start mb-5">
          <div>
            <h1 className="text-lg text-slate-dark font-semibold">
              {props.title || 'Link Template Fragments'}
            </h1>
            <h2 className="text-2xl text-navy">1. Select Templates</h2>
          </div>
          <BubbleButton
            onClick={props.onClose}
            className="ml-auto"
            variant="navy"
            inverse
            icon="xmark-large"
            iconSize="xl"
          />
        </div>
        <BubbleTable
          headVariant="velvet"
          responsive
          stickyHeader
          striped
          fields={tableFields}
          items={tableItemsFiltered}
          templates={tableTemplates}
          showPretitle
          pretitleClassName="p-0-i"
          pretitleRender={() => (
            <div>
              <div className="px-4 pt-4 mb-1 flex items-center">
                <BubbleInput
                  variant="velvet"
                  block
                  type="text"
                  value={search}
                  inputClassName="outline-none-i"
                  className="flex-grow"
                  placeholder="Search..."
                  onValue={setSearch}
                />
                <BubbleButton
                  variant="white"
                  inverse
                  className="-mr-4"
                  onClick={() => setShowFilters(!showFilters)}
                  iconSuffix={showFilters ? 'chevron-up' : 'chevron-down'}
                  iconClasses="mr-1"
                  icon="filter"
                />
              </div>
              {showFilters && (
                <div className="bg-velvet-dark p-4 text-white font-normal normal-case mt-3">
                  <div className="grid gap-4">
                    <BubbleFormGroup label="Tag" labelClassName="text-white-i">
                      <BubbleTypeahead
                        labelExtractor={itm => itm.name}
                        variant="velvet"
                        value={filters.tag}
                        options={tagOptions}
                        block
                        placeholder="Search..."
                        menuClassName="max-h-80 overflow-y-auto"
                        onAdd={tag => {
                          setFilters({ ...filters, tag: [...filters.tag, tag] });
                        }}
                        onRemove={tag => {
                          setFilters({
                            ...filters,
                            tag: filters.tag.filter(t => t.key !== tag.key),
                          });
                        }}
                        onClearAll={() => setFilters({ ...filters, tag: [] })}
                      />
                    </BubbleFormGroup>
                    {showType && (
                      <BubbleFormGroup label="Departing" labelClassName="text-white-i">
                        <BubbleSelect
                          allowUndefined
                          undefinedLabel="Any"
                          variant="velvet"
                          block
                          options={templateTypeOpts}
                          value={filters.type}
                          labelExtractor={opt => opt.toUpperCase()}
                          onValue={opt => {
                            setFilters({ ...filters, type: opt });
                          }}
                        />
                      </BubbleFormGroup>
                    )}
                    <div>
                      <BubbleInput
                        className="mr-2"
                        variant="velvet"
                        type="checkbox"
                        checked={filters.selectedOnly}
                        onValue={val => setFilters({ ...filters, selectedOnly: val })}
                      />
                      <span>Show Selected Only</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        />
      </FormGenericContainer>
    </div>
  );
}
